import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { ComboBox, type ComboOption } from '../../components/ComboBox';
import { Dialog } from '../../components/Dialog';
import { Stepper } from '../../components/Stepper';
import { DO_DATABASES, type DoDatabaseOption } from '../../lib/atlas';
import {
  addTargetReady,
  createTargetInput,
  deriveTargetKey,
  DO_SCAN_HINT,
  doDatabaseLabel,
  EMPTY_TARGET_FORM,
  isValidTargetKey,
  KIND_PICK_LABEL,
  type AddTargetForm,
  type TargetKind,
} from '../../lib/targetKind';

/**
 * Add access-list target — a two-step wizard in a modal.
 *
 * REPLACES the inline `AddProjectForm` that used to unfold under the capacity band.
 * Owner, 2026-09-07: the form "renders inline and looks unprofessional — it must be a
 * popup". It is also the only place in this app where a person types a CREDENTIAL, so
 * the surface it is typed on should be the one that is unambiguously in front of
 * everything else and has one way out.
 *
 * WHY TWO STEPS AND NOT ONE LONGER FORM. The kind decides which of two disjoint field
 * sets exists — an Atlas project id + OAuth pair, or a scanned DO database + API
 * token — and the earlier single form showed the answer changing under the operator's
 * cursor as they flipped the radio. Step 1 asks the question that determines the rest
 * (kind, and WHICH database or project); step 2 is only ever the naming and the
 * credential. It also makes the derived key possible: by step 2 the team is known, so
 * the name can propose a key that is already unique.
 *
 * VALIDATION TIMING. On Next and on blur, never while typing — a key half-typed is
 * not a wrong key, and an error that appears on the third character and disappears on
 * the eighth is noise. Back never validates.
 *
 * THE KEY IS PERMANENT. `UpdateAtlasProjectInput` has no `projectKey`, so a typo here
 * cannot be renamed later — only a second target and a dead first one. Hence the blur
 * probe against `atlasProject`, on top of the keys already on screen: the local list
 * is the overview the band already loaded, which does not include INACTIVE targets on
 * every path, and the server's duplicate error is a worse place to learn it.
 */

const CREATE_PROJECT = gql`
  mutation CreateAtlasProject($input: CreateAtlasProjectInput!) {
    createAtlasProject(input: $input) {
      projectKey
      hasCredentials
    }
  }
`;

/** Cheapest "is this key taken" question the schema can answer. */
const TARGET_EXISTS = gql`
  query AtlasTargetExists($projectKey: String!) {
    atlasProject(projectKey: $projectKey) {
      projectKey
    }
  }
`;

const STEPS = ['Kind', 'Details'];

type KeyCheck = { key: string; state: 'checking' | 'free' | 'taken' };

export function AddTargetWizard({
  open,
  onClose,
  existingKeys,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  /** The keys already on screen — checked before the network is asked at all. */
  existingKeys: string[];
  onDone: (projectKey: string) => void;
}) {
  const [form, setForm] = useState<AddTargetForm>(EMPTY_TARGET_FORM);
  const [step, setStep] = useState(0);
  // Once the operator edits the key it is theirs: re-deriving it from a later edit of
  // the display name would silently overwrite a deliberate choice.
  const [keyTouched, setKeyTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [keyCheck, setKeyCheck] = useState<KeyCheck | null>(null);
  // A Create press that arrived while the key probe was still in flight, REMEMBERED
  // (owner decision e-22). See the block above `submit()` for why this exists at all.
  const [queuedSubmit, setQueuedSubmit] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // A STEP CHANGE MOVES FOCUS, the same way opening the dialog does. `Dialog` seeds
  // focus once, on open; the primary button is then the focused element and it is
  // REPLACED on Next (Next → Create) — React unmounts the focused node, focus falls
  // to <body>, and the next Tab starts at the top of the page instead of in the form.
  // Same `[data-autofocus]` convention Dialog uses, so each step nominates its own
  // first field: the kind radio on step 1, Display name on step 2.
  useEffect(() => {
    if (!open) return;
    bodyRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [open, step]);

  const isDo = form.kind === 'do_db';

  // Only asked for once DigitalOcean is chosen: an Atlas add must not depend on a
  // query that an api predating 052 cannot answer.
  const dbs = useQuery<{ doDatabases: DoDatabaseOption[] }>(DO_DATABASES, {
    skip: !isDo,
    fetchPolicy: 'cache-and-network',
  });
  const dbRows = dbs.data?.doDatabases ?? [];

  const [probe] = useLazyQuery<{ atlasProject: { projectKey: string } | null }>(TARGET_EXISTS, {
    fetchPolicy: 'network-only',
  });

  const [create, createState] = useMutation<{ createAtlasProject: { projectKey: string } }>(
    CREATE_PROJECT,
    { onCompleted: (d) => finish(d.createAtlasProject.projectKey) },
  );

  const reset = () => {
    setForm(EMPTY_TARGET_FORM);
    setStep(0);
    setKeyTouched(false);
    setErrors({});
    setKeyCheck(null);
    setQueuedSubmit(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const finish = (key: string) => {
    reset();
    onDone(key);
  };

  const set =
    (k: keyof AddTargetForm) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  // Switching kind clears the kind-specific fields so a client id typed for Atlas
  // cannot ride along into a do_db create (the server refuses a non-empty one).
  // The derived key carries the kind's team prefix, so it is stale the moment either
  // changes — after Back, a do_db key like `optisigns-lite-prod-pg` would otherwise
  // survive a switch to Atlas, where no such team exists. Only while the operator has
  // not taken the key over: after that it is theirs, stale or not.
  const rederiveKey = (f: AddTargetForm, kind: TargetKind, doTeam: string) =>
    keyTouched ? f.projectKey : deriveTargetKey(f.displayName, kind, doTeam);

  const pickKind = (kind: TargetKind) => {
    setForm((f) => ({
      ...f,
      kind,
      projectId: '',
      clientId: '',
      clientSecret: '',
      doTeam: '',
      projectKey: rederiveKey(f, kind, ''),
    }));
    setErrors({});
    setKeyCheck(null);
  };

  // The team is DERIVED from the chosen database, never typed: a target whose
  // `do_team` disagrees with the database it points at would hold the wrong token.
  const pickDb = (uuid: string) => {
    const d = dbRows.find((r) => r.uuid === uuid);
    const doTeam = d?.doTeam ?? '';
    setForm((f) => {
      // The database's name is the display name people would type anyway. It stays
      // fully editable on step 2 — this only saves the typing.
      const displayName = f.displayName || d?.name || '';
      return {
        ...f,
        projectId: uuid,
        doTeam,
        displayName,
        projectKey: rederiveKey({ ...f, displayName }, f.kind, doTeam),
      };
    });
    setErrors((e) => ({ ...e, projectId: '' }));
    setKeyCheck(null);
  };

  /** The scanned rows, and only those (owner e-13): no synthetic "use this id". */
  const dbOptions: ComboOption[] = dbRows.map((d) => ({
    value: d.uuid,
    label: doDatabaseLabel(d),
    // A pasted uuid selects its row — the reason there is no free-text uuid field.
    keywords: [d.uuid, d.name, d.doTeam, d.engine ?? ''],
  }));

  const goNext = () => {
    const next: Record<string, string> = {};
    if (isDo && !form.projectId) next.projectId = 'Choose one of the scanned databases.';
    if (!isDo && !form.projectId.trim()) next.projectId = 'The Atlas project id is required.';
    setErrors(next);
    if (Object.keys(next).length) return;
    // Propose the key from whatever name we have, the first time only.
    setForm((f) => ({
      ...f,
      projectKey:
        keyTouched || f.projectKey ? f.projectKey : deriveTargetKey(f.displayName, f.kind, f.doTeam),
    }));
    setStep(1);
  };

  /** Name → key, until the operator takes the key over. */
  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const displayName = e.target.value;
    setForm((f) => ({
      ...f,
      displayName,
      projectKey: keyTouched ? f.projectKey : deriveTargetKey(displayName, f.kind, f.doTeam),
    }));
    setKeyCheck(null);
  };

  const checkKey = async () => {
    const key = form.projectKey.trim();
    if (!key) return;
    if (!isValidTargetKey(key)) {
      setErrors((e) => ({
        ...e,
        projectKey: 'Lowercase letters, digits and single hyphens only — 2 to 64 characters.',
      }));
      setKeyCheck(null);
      // A queued Create is DROPPED, not deferred: the key it was queued against is
      // not a key at all, and the error beneath the field is the answer.
      setQueuedSubmit(false);
      return;
    }
    setErrors((e) => ({ ...e, projectKey: '' }));
    if (existingKeys.includes(key)) {
      setKeyCheck({ key, state: 'taken' });
      setQueuedSubmit(false);
      return;
    }
    // The blur that starts this probe is usually the click on Create. Until it
    // answers, "is this key taken" is unanswered, and a create sent now would race
    // it — so a press that lands here is QUEUED, not refused (see `pressCreate`).
    setKeyCheck({ key, state: 'checking' });
    const res = await probe({ variables: { projectKey: key } }).catch(() => null);
    // A NotFound error is the answer "free". Any OTHER failure must not block a
    // create — the server's own duplicate error is the backstop, and refusing on a
    // network blip would be a form that cannot be submitted for a reason it cannot
    // explain.
    if (res?.data?.atlasProject) setKeyCheck({ key, state: 'taken' });
    else setKeyCheck({ key, state: 'free' });
  };

  const keyIs = (state: KeyCheck['state']) =>
    keyCheck?.state === state && keyCheck.key === form.projectKey.trim();
  const keyTaken = keyIs('taken');
  const keyChecking = keyIs('checking');
  /** Everything except the probe's answer. The probe is a RACE, not a requirement. */
  const formReady = addTargetReady(form) && !keyTaken && !errors.projectKey;

  const submit = () => void create({ variables: { input: createTargetInput(form) } });

  /**
   * THE PRESS THAT USED TO BE SWALLOWED (owner decision e-22).
   *
   * The key probe starts on BLUR, and the blur that starts it is the same pointer
   * gesture as the click on Create. Sequence: mousedown → blur → `checking` →
   * re-render. When `checking` disabled the button, the mouseup landed on a disabled
   * control, no `click` was ever dispatched, and the operator's press vanished with
   * nothing on screen to say why. Pressing again worked, which made it look random.
   *
   * So the button STAYS pressable while checking, and the press is remembered: when
   * the probe answers `free` for the key that is still in the box, the create fires
   * by itself. `taken` or an invalid key drops the queue — the reason is already
   * rendered under the field. The copy stays "Checking key…" the whole time, so the
   * queued state is not a second, invisible mode.
   */
  useEffect(() => {
    if (!queuedSubmit || !keyCheck) return;
    if (keyCheck.key !== form.projectKey.trim()) return;
    if (keyCheck.state === 'checking') return;
    setQueuedSubmit(false);
    if (keyCheck.state === 'free' && formReady) submit();
    // `submit`/`formReady` are recomputed every render from the same state this
    // effect reads; keying on the probe result is what makes it fire exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedSubmit, keyCheck]);

  const pressCreate = () => {
    if (keyChecking) {
      setQueuedSubmit(true);
      return;
    }
    submit();
  };

  const field = (
    label: string,
    key: keyof AddTargetForm,
    placeholder: string,
    hint?: string,
    secret?: boolean,
  ) => (
    <label className="wizard-field">
      <div className="kv-k">{label}</div>
      <input
        className="class-select"
        type={secret ? 'password' : 'text'}
        autoComplete={secret ? 'new-password' : 'off'}
        placeholder={placeholder}
        value={form[key]}
        onChange={set(key)}
      />
      {hint && <div className="wizard-hint muted">{hint}</div>}
    </label>
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add access-list target"
      titleId="add-target-title"
      size="md"
      busy={createState.loading}
    >
      <Stepper steps={STEPS} current={step} />

      <div className="dialog-body" ref={bodyRef}>
        {step === 0 ? (
          <>
            <fieldset className="kind-cards">
              <legend className="kv-k wizard-legend">Kind *</legend>
              {(['atlas', 'do_db'] as TargetKind[]).map((k, i) => (
                <label className="kind-card" key={k}>
                  <input
                    type="radio"
                    name="target-kind"
                    value={k}
                    checked={form.kind === k}
                    onChange={() => pickKind(k)}
                    data-autofocus={i === 0 || undefined}
                  />
                  {KIND_PICK_LABEL[k]}
                </label>
              ))}
            </fieldset>

            {isDo ? (
              <div className="wizard-field">
                <div className="kv-k" id="add-db-label">
                  Database *
                </div>
                {dbs.loading && !dbs.data ? (
                  <div className="wizard-hint muted">Loading scanned databases…</div>
                ) : dbs.error ? (
                  <div className="wizard-hint error">
                    Couldn&apos;t list the scanned databases — {dbs.error.message}
                  </div>
                ) : dbRows.length === 0 ? (
                  // AC-19: the hint replaces the picker. No uuid input exists here.
                  <div className="wizard-hint muted" data-testid="do-scan-hint">
                    {DO_SCAN_HINT}
                  </div>
                ) : (
                  <>
                    <ComboBox
                      id="add-db"
                      label="Database"
                      placeholder="Type a database name, or paste its uuid…"
                      options={dbOptions}
                      value={form.projectId}
                      onPick={pickDb}
                      emptyText="No scanned database matches — the list is the last do-db-firewall scan."
                      describedBy="add-db-hint"
                    />
                    <div className="wizard-hint muted" id="add-db-hint">
                      From the last <code>do-db-firewall</code> scan
                      {form.doTeam ? (
                        <>
                          {' '}
                          · team <span className="mono">{form.doTeam}</span>
                        </>
                      ) : null}
                    </div>
                  </>
                )}
                {errors.projectId && <div className="wizard-hint error">{errors.projectId}</div>}
              </div>
            ) : (
              <div className="wizard-field">
                {field(
                  'Atlas project id *',
                  'projectId',
                  '64a1b2c3d4e5f6789012345',
                  'Atlas → Project Settings → General',
                )}
                {errors.projectId && <div className="wizard-hint error">{errors.projectId}</div>}
              </div>
            )}
          </>
        ) : (
          <>
            <label className="wizard-field">
              <div className="kv-k">Display name</div>
              <input
                className="class-select"
                type="text"
                autoComplete="off"
                data-autofocus
                placeholder={isDo ? 'Production Postgres' : 'Production Atlas'}
                value={form.displayName}
                onChange={onNameChange}
              />
              <div className="wizard-hint muted">
                What this target is called on screen. The key below follows it until you
                edit the key yourself.
              </div>
            </label>

            <label className="wizard-field">
              <div className="kv-k">{isDo ? 'Target key *' : 'Project key *'}</div>
              <input
                className="class-select mono"
                type="text"
                autoComplete="off"
                placeholder={isDo ? 'prod-do-postgres' : 'prod-atlas'}
                value={form.projectKey}
                onChange={(e) => {
                  setKeyTouched(true);
                  setKeyCheck(null);
                  // Editing the key retracts a press queued against the old one.
                  setQueuedSubmit(false);
                  setErrors((x) => ({ ...x, projectKey: '' }));
                  setForm((f) => ({ ...f, projectKey: e.target.value }));
                }}
                onBlur={() => void checkKey()}
                aria-invalid={keyTaken || Boolean(errors.projectKey) || undefined}
              />
              {errors.projectKey ? (
                <div className="wizard-hint error">{errors.projectKey}</div>
              ) : keyTaken ? (
                <div className="wizard-hint error">
                  A target with the key <span className="mono">{form.projectKey.trim()}</span>{' '}
                  already exists — pick another. A key cannot be changed after the target is
                  created.
                </div>
              ) : (
                <div className="wizard-hint muted">
                  Permanent identifier used by agents and in this target&apos;s URL. It cannot be
                  changed later.
                </div>
              )}
            </label>

            {isDo ? (
              field(
                'DO API token *',
                'clientSecret',
                'dop_v1_…',
                'Needs database:read + database:update on that team. Stored encrypted (never plaintext)',
                true,
              )
            ) : (
              <>
                {field(
                  'OAuth client id *',
                  'clientId',
                  'mdb_sa_id_…',
                  'Atlas → Access Manager → Applications',
                )}
                {field(
                  'OAuth client secret *',
                  'clientSecret',
                  'Enter client secret',
                  'Stored encrypted (never plaintext)',
                  true,
                )}
              </>
            )}

            <label className="wizard-field">
              <div className="kv-k">Description</div>
              <textarea
                className="class-select"
                rows={2}
                placeholder={
                  isDo ? 'optisigns-internal on optisigns-lite' : 'Production MongoDB Atlas project'
                }
                value={form.description}
                onChange={set('description')}
              />
            </label>

            {createState.error && <p className="error">{createState.error.message}</p>}
          </>
        )}
      </div>

      <div className="dialog-actions">
        <button type="button" className="btn" onClick={close} disabled={createState.loading}>
          Cancel
        </button>
        <span className="spacer" />
        {step === 1 && (
          <button
            type="button"
            className="btn"
            /* Back never validates — going back to change the answer is exactly
               what a person does when the answer was wrong. */
            onClick={() => setStep(0)}
            disabled={createState.loading}
          >
            Back
          </button>
        )}
        {step === 0 ? (
          <button type="button" className="btn btn-primary" onClick={goNext}>
            Next
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            /* NOT disabled while the probe runs — see `pressCreate`. */
            disabled={!formReady || createState.loading}
            aria-busy={keyChecking || queuedSubmit || undefined}
            onClick={pressCreate}
          >
            {createState.loading
              ? 'Creating…'
              : keyChecking || queuedSubmit
                ? 'Checking key…'
                : 'Create'}
          </button>
        )}
      </div>
    </Dialog>
  );
}
